import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, RotateCcw } from 'lucide-react';

const API = '/api';
export const TIER_OPTIONS = [
  { value: 'targeted', label: 'Targeted' },
  { value: 'regional_partner', label: 'Regional partner' },
  { value: 'exploratory', label: 'Exploratory' },
];
const MIN = 10;
// Worked examples per tier — copy, adapt, paste. Good rationales name the
// concrete link (franchise, indication, territory, expansion angle) and why
// it matters for THIS asset; that is also what the engine learns from.
export const RATIONALE_EXAMPLES = {
  targeted: 'Active in serious immune-mediated inflammatory diseases incl. hidradenitis suppurativa, which is one of this asset\'s expansion indications; a clinical-stage IL-36R biologic fits their in-licensing model.',
  regional_partner: 'Regional specialty pharma with dermatology commercial infrastructure in Europe / LATAM; open to territory licensing of approved biologics — the HS / lupus-nephritis expansion pipeline adds upside for a rare-disease launch.',
  exploratory: 'Not a core fit today, but their GPCR / rare-dermatology capability means the target biology is understood; worth one exploratory touch to test appetite before the Phase 3 read-out.',
};

// Reinstate one or many quality-gate refusals: tier + mandatory rationale per company.
export const ReinstateDialog = ({ data, onClose, onDone }) => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setRows((data?.items || []).map(it => ({
      company_name: it.company_name,
      tier: it.defaultTier || 'targeted',
      rationale: (it.rationale || '').trim(),
      why: it.why || '',
    })));
  }, [data]);
  const ctx = data?.ctx || {};
  const invalid = rows.filter(r => r.rationale.trim().length < MIN);
  const update = (i, patch) => setRows(prev => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const submit = async () => {
    if (invalid.length) { toast.error(`Add a rationale (≥ ${MIN} characters) for every company`); return; }
    setBusy(true);
    try {
      const { data: res } = await axios.post(`${API}/ai/reinstatements`, {
        biotech_company_id: ctx.biotech_company_id, asset_id: ctx.asset_id, mapping_id: ctx.mapping_id || '',
        items: rows.map(r => ({ company_name: r.company_name, tier: r.tier, rationale: r.rationale.trim() })),
      }, { withCredentials: true });
      toast.success(`Reinstated ${rows.length} compan${rows.length === 1 ? 'y' : 'ies'} — flagged "Manually reinstated" and kept on every re-run`);
      onDone?.(rows, res);
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (e?.message || 'Reinstate failed'), { duration: 9000 });
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!data} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="reinstate-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2"><RotateCcw className="w-4 h-4 text-emerald-600" /> Reinstate {rows.length === 1 ? rows[0]?.company_name : `${rows.length} companies`}</DialogTitle>
          <p className="text-xs text-slate-500">Overrules the quality gate for this asset. Pick the tier and give a rationale — it is stored, flagged <span className="font-semibold">Manually reinstated</span>, respected on every re-run, <span className="font-semibold">and learned</span>: on future assets of the same kind the engine keeps this company in and carries your reason forward as a "BD note".</p>
          <details className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5" data-testid="reinstate-examples">
            <summary className="cursor-pointer font-semibold">What a good rationale looks like (examples to copy)</summary>
            <ul className="mt-1.5 space-y-1">
              {TIER_OPTIONS.map(o => (
                <li key={o.value}><span className="font-semibold">{o.label}:</span> “{RATIONALE_EXAMPLES[o.value]}”</li>
              ))}
            </ul>
            <p className="mt-1 text-[10px] text-slate-500">Name the concrete link — franchise, indication, territory or expansion angle — and why it matters for this asset. Avoid "could be interested".</p>
          </details>
        </DialogHeader>
        <div className="space-y-2.5">
          {rows.map((r, i) => (
            <div key={r.company_name} className="border border-slate-200 rounded-md p-2.5 space-y-1.5 bg-white" data-testid={`reinstate-row-${r.company_name}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-slate-800 truncate">{r.company_name}</p>
                  {r.why && <p className="text-[10px] text-amber-700 truncate" title={r.why}>Gate: {r.why}</p>}
                </div>
                <select value={r.tier} onChange={e => update(i, { tier: e.target.value })} className="h-7 text-[11px] border border-slate-300 rounded px-1.5 bg-white" data-testid={`reinstate-tier-${r.company_name}`}>
                  {TIER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-slate-500">Why should this company be in outreach? (required)</span>
                <button type="button" onClick={() => update(i, { rationale: r.rationale.trim() ? r.rationale : RATIONALE_EXAMPLES[r.tier] })} className="text-[10px] text-sky-700 hover:underline" data-testid={`reinstate-use-example-${r.company_name}`}>
                  {r.rationale.trim() ? 'Example shown above' : `Use ${TIER_OPTIONS.find(o => o.value === r.tier)?.label} example`}
                </button>
              </div>
              <textarea value={r.rationale} onChange={e => update(i, { rationale: e.target.value })} rows={2} placeholder={RATIONALE_EXAMPLES[r.tier]}
                className={`w-full text-[11px] border rounded px-2 py-1 resize-y ${r.rationale.trim().length < MIN ? 'border-rose-300 bg-rose-50/40' : 'border-slate-300'}`} data-testid={`reinstate-rationale-${r.company_name}`} />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between pt-2">
          <span className="text-[10px] text-slate-500">{invalid.length ? `${invalid.length} still need a rationale` : 'Every company has a rationale'}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={onClose} data-testid="reinstate-cancel-btn">Cancel</Button>
            <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={submit} disabled={busy || !rows.length} data-testid="reinstate-submit-btn">
              {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />} Reinstate {rows.length}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
